import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import styles from "./AppVersionForm.module.css";
import {
  CustomButton,
  CustomDropdown,
  CustomDatePicker,
  CustomTextInput,
} from "../../components";
import PopupModal from "../../components/PopupModal/PopupModal";
import AxiosInstance, { TokenService } from "../../services/Api";

const OS_TYPE_OPTIONS = [
  { value: "Android", label: "Android" },
  { value: "iOS", label: "iOS" },
];

const BUILD_TYPE_OPTIONS = [
  { value: "Test", label: "Test" },
  { value: "Production", label: "Production" },
];

const STATUS_OPTIONS = [
  { value: "1", label: "Active" },
  { value: "0", label: "Inactive" },
];
const DEFAULT_STATUS_OPTION = STATUS_OPTIONS[1];

const VERSION_REGEX = /^[0-9.]+$/;

// Emoji detection (matches pictographic emoji characters)
const EMOJI_REGEX = /\p{Extended_Pictographic}/u;

const isSuccess = (response) =>
  response?.data?.status === true || response?.data?.success === true;

/** Turn legacy API messages into plain “Version” wording + short hint. */
const normalizeApiErrorMessage = (msg) => {
  if (msg == null) return "";
  let text = String(msg).trim();
  if (!text) return "";
  if (/android\s+version\s+is\s+required/i.test(text)) {
    return "Version is required. The server is still using an old check name (android_version). It should validate your Version field with Platform only.";
  }
  if (/ios\s+version\s+is\s+required/i.test(text)) {
    return "Version is required. The server is still using an old check name (ios_version). It should validate your Version field with Platform only.";
  }
  text = text.replace(/\bAndroid\s+version\b/gi, "Version");
  text = text.replace(/\biOS\s+version\b/gi, "Version");
  text = text.replace(/\bIOS\s+version\b/gi, "Version");
  return text.trim();
};

const pickApiMessage = (data) => {
  if (!data || typeof data !== "object") return "";
  const m =
    data.message ??
    data.error ??
    (typeof data.errors === "string" ? data.errors : null);
  if (typeof m === "string" && m.trim()) return m.trim();
  if (Array.isArray(data.errors) && data.errors.length) {
    return data.errors.map((e) => (typeof e === "string" ? e : e?.message || "")).filter(Boolean).join(" ");
  }
  return "";
};

const LIST_PATH = "/dashboard/appversion";

const hasDateValue = (date) => {
  if (!date) return false;
  if (dayjs.isDayjs(date)) return date.isValid();
  return Boolean(String(date).trim());
};

const getStatusFromReleasedDate = (releasedDate) =>
  hasDateValue(releasedDate) ? STATUS_OPTIONS[0] : DEFAULT_STATUS_OPTION;

const formatApiDate = (date) => {
  const d = dayjs(date);
  return d.isValid() ? d.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
};

const parseFormDate = (value) => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const sanitizeFutureDate = (value) => {
  const parsed = parseFormDate(value);
  if (parsed && parsed.isAfter(dayjs(), "day")) return null;
  return parsed;
};

const toDropdownOption = (value, options) => {
  if (!value) return null;
  const normalized = String(value).trim();
  return (
    options.find(
      (opt) => opt.value.toLowerCase() === normalized.toLowerCase(),
    ) || { value: normalized, label: normalized }
  );
};

/** One Version in UI/API; os_type picks platform. Legacy android_version/ios_version only when version absent. */
const mapItemToForm = (item) => {
  let osType = item.os_type;
  let version =
    item.version != null && String(item.version).trim() !== ""
      ? String(item.version).trim()
      : "";

  if (!version) {
    if (item.android_version != null && item.android_version !== "") {
      version = String(item.android_version);
      if (!osType) osType = "Android";
    } else if (item.ios_version) {
      version = String(item.ios_version);
      if (!osType) osType = "iOS";
    }
  }

  const submittedDate =
    sanitizeFutureDate(item.submitted_date || item.created_date) || dayjs();
  let releasedDate = sanitizeFutureDate(item.released_date);

  if (
    submittedDate &&
    releasedDate &&
    dayjs(submittedDate).isAfter(dayjs(releasedDate), "day")
  ) {
    releasedDate = null;
  }

  return {
    os_type: toDropdownOption(osType, OS_TYPE_OPTIONS),
    version,
    build_type: toDropdownOption(item.build_type || item.type, BUILD_TYPE_OPTIONS),
    submitted_date: submittedDate,
    released_date: releasedDate,
    force_update:
      item.force_update === true ||
      item.force_update === 1 ||
      item.force_update === "1",
    status: getStatusFromReleasedDate(releasedDate),
  };
};

const getInitialFormState = () => ({
  os_type: null,
  version: "",
  build_type: null,
  submitted_date: dayjs(),
  released_date: null,
  force_update: false,
  status: DEFAULT_STATUS_OPTION,
});

function AppVersionForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState(getInitialFormState);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

  const fetchVersion = useCallback(async () => {
    if (!id) return;
    setFetchLoading(true);
    try {
      const response = await AxiosInstance.post("/app-version/view", {
        id: Number(id),
      });
      const item =
        response?.data?.app_version ||
        response?.data?.appVersion ||
        response?.data?.data;

      if (isSuccess(response) && item) {
        setFormData(mapItemToForm(item));
      } else {
        throw new Error(response?.data?.message || "Failed to fetch app version");
      }
    } catch (err) {
      setModalState({
        open: true,
        type: "error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Error fetching app version",
        redirectPath: LIST_PATH,
      });
    } finally {
      setFetchLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEditMode) fetchVersion();
  }, [isEditMode, fetchVersion]);

  const clearFieldError = useCallback(
    (name) => {
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
    },
    [errors],
  );

  const handleInputChange = useCallback(
    (e) => {
      const { name, value, type, checked } = e.target;
      let nextValue = type === "checkbox" ? checked : value;

      if (name === "version" && typeof nextValue === "string") {
        nextValue = nextValue.replace(/[^0-9.]/g, "");
      }

      setFormData((prev) => ({ ...prev, [name]: nextValue }));
      clearFieldError(name);
    },
    [clearFieldError],
  );

  const handleDropdownChange = useCallback(
    (name, value) => {
      setFormData((prev) => ({ ...prev, [name]: value }));
      clearFieldError(name);
    },
    [clearFieldError],
  );

  const validateDatePair = useCallback((submittedDate, releasedDate) => {
    if (
      hasDateValue(submittedDate) &&
      hasDateValue(releasedDate) &&
      dayjs(submittedDate).isAfter(dayjs(releasedDate), "day")
    ) {
      return "Submitted date cannot be after released date";
    }
    return "";
  }, []);

  const handleSubmittedDateChange = useCallback(
    (value) => {
      if (value && dayjs(value).isAfter(dayjs(), "day")) {
        setErrors((prev) => ({
          ...prev,
          submitted_date: "Submitted date cannot be in the future",
        }));
        return;
      }

      const pairError = validateDatePair(value, formData.released_date);
      setFormData((prev) => ({ ...prev, submitted_date: value }));
      setErrors((prev) => ({
        ...prev,
        submitted_date: pairError,
        released_date: pairError && prev.released_date ? pairError : prev.released_date,
      }));
      if (!pairError) clearFieldError("submitted_date");
    },
    [formData.released_date, validateDatePair, clearFieldError],
  );

  const handleReleasedDateChange = useCallback(
    (value) => {
      if (value && dayjs(value).isAfter(dayjs(), "day")) {
        setErrors((prev) => ({
          ...prev,
          released_date: "Released date cannot be in the future",
        }));
        return;
      }

      const pairError = validateDatePair(formData.submitted_date, value);
      setFormData((prev) => ({
        ...prev,
        released_date: value,
        status: getStatusFromReleasedDate(value),
      }));
      setErrors((prev) => ({
        ...prev,
        released_date: pairError,
        submitted_date:
          pairError && prev.submitted_date ? pairError : prev.submitted_date,
      }));
      if (!pairError) clearFieldError("released_date");
    },
    [formData.submitted_date, validateDatePair, clearFieldError],
  );

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.os_type?.value && !formData.os_type) {
      newErrors.os_type = "Select Android or iOS for this build (one only)";
    }

    const version = String(formData.version).trim();
    if (!version) {
      newErrors.version = "Version is required";
    } else if (!VERSION_REGEX.test(version)) {
      newErrors.version = "Version can only contain numbers and dots (e.g. 1.3.0)";
    }

    if (!formData.build_type?.value && !formData.build_type) {
      newErrors.build_type = "Build type is required";
    }

    if (!hasDateValue(formData.submitted_date)) {
      newErrors.submitted_date = "Submitted date is required";
    } else if (dayjs(formData.submitted_date).isAfter(dayjs(), "day")) {
      newErrors.submitted_date = "Submitted date cannot be in the future";
    }

    if (
      formData.released_date &&
      dayjs(formData.released_date).isAfter(dayjs(), "day")
    ) {
      newErrors.released_date = "Released date cannot be in the future";
    }

    const pairError = validateDatePair(
      formData.submitted_date,
      formData.released_date,
    );
    if (pairError) {
      newErrors.submitted_date = pairError;
      newErrors.released_date = pairError;
    }

    // ✅ Version — emoji & space safety guard (additive; never overrides above)
    if (!newErrors.version) {
      const rawVersion = String(formData.version);
      if (rawVersion && EMOJI_REGEX.test(rawVersion)) {
        newErrors.version = "Version cannot contain emojis";
      } else if (rawVersion && /\s/.test(rawVersion)) {
        newErrors.version = "Version cannot contain spaces";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, validateDatePair]);

  const preparePayload = useCallback(() => {
    const user = TokenService.getUser();
    const userId = user?.id || user?.user_id || 1;

    const osType = formData.os_type?.value ?? formData.os_type ?? "";
    const buildType = formData.build_type?.value ?? formData.build_type ?? "";
    const releasedDate = hasDateValue(formData.released_date)
      ? formatApiDate(formData.released_date)
      : "";

    const versionStr = String(formData.version).trim();
    const osLower = String(osType).trim().toLowerCase();

    const payload = {
      os_type: String(osType).trim(),
      version: versionStr,
      build_type: String(buildType).trim(),
      submitted_date: formatApiDate(formData.submitted_date),
      released_date: releasedDate,
      force_update: Boolean(formData.force_update),
      status: getStatusFromReleasedDate(formData.released_date).value,
      user_id: String(userId),
    };

    // Legacy API checks often expect android_version OR ios_version; mirror Version only for the selected platform.
    if (osLower === "android") {
      payload.android_version = versionStr;
    } else if (osLower === "ios") {
      payload.ios_version = versionStr;
    }

    if (isEditMode) {
      payload.id = Number(id);
    }

    return payload;
  }, [formData, id, isEditMode]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const payload = preparePayload();
      const response = isEditMode
        ? await AxiosInstance.post("/app-version/edit", payload)
        : await AxiosInstance.post("/app-version/create", payload);

      if (isSuccess(response)) {
        setModalState({
          open: true,
          type: "success",
          message:
            response?.data?.message ||
            (isEditMode
              ? "App version updated successfully."
              : "App version created successfully."),
          redirectPath: LIST_PATH,
        });
      } else {
        throw new Error(
          normalizeApiErrorMessage(pickApiMessage(response?.data)) ||
            `Failed to ${isEditMode ? "update" : "create"} app version`,
        );
      }
    } catch (err) {
      const raw =
        pickApiMessage(err?.response?.data) ||
        err?.response?.data?.message ||
        err?.message;
      setModalState({
        open: true,
        type: "error",
        message:
          normalizeApiErrorMessage(raw) ||
          `Error ${isEditMode ? "updating" : "creating"} app version`,
        redirectPath: "",
      });
    } finally {
      setLoading(false);
    }
  }, [validateForm, preparePayload, isEditMode]);

  const handleModalOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };

  const releasedMinDate = hasDateValue(formData.submitted_date)
    ? dayjs(formData.submitted_date)
    : undefined;

  if (fetchLoading) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.loadingContainer}>
          <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: "24px" }} />
          <span style={{ marginLeft: "10px" }}>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleModalOk}
      />

      <div className={styles.pageWrapper}>
        <div className={styles.breadcrumbHeader}>
          <div className={styles.breadcrumb}>
            <span className={styles.breadcrumbLink}>Home</span>
            <span className={styles.breadcrumbSeparator}>&gt;</span>
            <span className={styles.breadcrumbCurrent}>
              {isEditMode ? "Edit App Version" : "App Version Creation"}
            </span>
          </div>
          <button
            type="button"
            className={styles.viewListButton}
            onClick={() => navigate(LIST_PATH)}
          >
            <i className="fa-solid fa-list" style={{ marginRight: "8px" }} />
            View List
          </button>
        </div>

        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>
            {isEditMode ? "Edit App Version" : "Create App Version"}
          </h2>

          <div className="row">

            
            <div className="col-lg-6 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label="Platform"
                name="os_type"
                value={formData.os_type}
                onChange={(value) => handleDropdownChange("os_type", value)}
                options={OS_TYPE_OPTIONS}
                placeholder="Android or iOS"
                required
                error={errors.os_type}
               
                size="md"
                isSearchable={false}
              />
            </div>
            
            <div className="col-lg-6 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label="Version"
                name="version"
                value={formData.version}
                onChange={handleInputChange}
                placeholder="e.g. 1.3.0"
                required
                error={errors.version}
               
                size="md"
              />
            </div>


            <div className="col-lg-6 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label="Build Type"
                name="build_type"
                value={formData.build_type}
                onChange={(value) => handleDropdownChange("build_type", value)}
                options={BUILD_TYPE_OPTIONS}
                placeholder="Select build type"
                required
                error={errors.build_type}
                size="md"
                isSearchable={false}
              />
            </div>

            <div className="col-lg-6 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label="Submitted Date"
                name="submitted_date"
                value={formData.submitted_date}
                onChange={handleSubmittedDateChange}
                placeholder="Select submitted date"
                format="DD/MM/YYYY"
                maxDate={dayjs()}
                required
                error={errors.submitted_date}
                size="sm"
                isClearable={false}
              />
            </div>

            <div className="col-lg-6 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label="Released Date"
                name="released_date"
                value={formData.released_date}
                onChange={handleReleasedDateChange}
                placeholder="Select released date"
                format="DD/MM/YYYY"
                minDate={releasedMinDate}
                maxDate={dayjs()}
                error={errors.released_date}
                size="sm"
                isClearable
              />
            </div>

            <div className="col-lg-6 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label="Status"
                name="status"
                value={formData.status}
                onChange={(value) => handleDropdownChange("status", value)}
                options={STATUS_OPTIONS}
                placeholder="Select status"
                size="md"
                isSearchable={false}
                isClearable={false}
                disabled
                helperText="Inactive when Released Date is empty; Active when a date is set."
              />
            </div>

            <div className="col-lg-6 px-2 col-md-6 col-sm-12">
              <label className={styles.checkboxLabel} htmlFor="force_update">
                Force Update
              </label>
              <div className={styles.checkboxRow}>
                <input
                  id="force_update"
                  name="force_update"
                  type="checkbox"
                  checked={formData.force_update}
                  onChange={handleInputChange}
                />
                <span className={styles.checkboxLabel}>
                  Require users to update the app
                </span>
              </div>
            </div>
          </div>

          <div className={styles.formActions}>
            <CustomButton
              variant="outline-dark"
              onClick={() => navigate(LIST_PATH)}
              fullWidth={false}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              loadingText={isEditMode ? "Updating..." : "Submitting..."}
              fullWidth={false}
            >
              {isEditMode ? "Update" : "Submit"}
            </CustomButton>
          </div>
        </div>
      </div>
    </>
  );
}

export default AppVersionForm;  